import * as React from 'react';
import Select from 'react-select';

/**
 * @typedef {{ label: string; value: string; }} Option
 */

/**
 * @param {{
 *   locationIds: number[];
 *   groupIds: number[];
 *   onChange: (value: Option | readonly Option[] | null) => void;
 *   value: Option | readonly Option[] | null;
 *   isMulti?: boolean;
 *   showInactive?: boolean;
 *   isLoading?: boolean;
 * }} props
 */
export default function EmployeeSelect({
  locationIds,
  groupIds,
  onChange,
  value,
  isMulti = false,
  showInactive = false,
  isLoading: isLoadingProp = false,
}) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [options, setOptions] = React.useState(/** @type {Option[]} */ ([]));

  async function fetchRepresentatives() {
    if (locationIds.length === 0 && groupIds.length === 0) {
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch(`/reports/representatives`, {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          location_ids: locationIds,
          group_ids: groupIds,
          show_inactive: showInactive,
        }),
      });

      /**
       * @type {{
       *   user_id: number;
       *   first_name: string;
       *   last_name: string;
       *   employee_code: string;
       * }[]}
       */
      const data = await res.json();
      const formattedOptions = data.map((representative) => ({
        label: `${representative.first_name} ${representative.last_name} (${representative.employee_code})`,
        value: `${representative.user_id}`,
      }));

      setOptions(formattedOptions);
    } catch (error) {
      console.error(error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Select
      styles={{
        container: (provided) => ({
          ...provided,
          flexGrow: 1,
          minWidth: 200,
          paddingRight: 8,
        }),
        control: (provided) => ({
          ...provided,
          minHeight: 34,
          boxShadow: 'none',
        }),
        dropdownIndicator: (provided) => ({
          ...provided,
          padding: 6,
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
      }}
      placeholder='Select Employee'
      isLoading={isLoading || isLoadingProp}
      options={options}
      onFocus={fetchRepresentatives}
      value={value}
      onChange={onChange}
      isMulti={isMulti}
      menuPortalTarget={document.body}
      menuPosition='fixed'
      maxMenuHeight={140}
      menuShouldBlockScroll
    />
  );
}
